import { memo, useCallback, useMemo } from 'react';
import { Filters, SelectableChip } from '@aimsos/ui';
import { useTranslation } from '@aimsos/i18n';

import {
  ADDED_FILTER_OPTIONS,
  MEMBERS_DEFAULT_SORT,
  MEMBERS_SORT_FIELDS,
  MEMBER_QUICK_FILTERS,
  MFA_FILTER,
} from '../../constants/membersFilters';
import { fromSlotValue, toSlotValue } from '../../utils/filterAdapters';
import { getSortForField } from '../../utils/getSortForField';

/** Stable empty list for the status slot (`Filters` is memoised). */
const NO_OPTIONS = Object.freeze([]);

/** Module-scope constant: `Filters` is memoised. */
const VIEW_TOGGLE_HIDDEN = Object.freeze({ hidden: true });

/**
 * Filter band of the Members list: search, Role / Group / MFA slots, sort and "All filters", plus
 * the quick-filter chips (All, Active, Invited, Not invited, Suspended, Removed). Holds no state:
 * it translates the `useMembersFilters` hook into `Filters` props and calls back.
 *
 * @param {object} props
 * @param {object} props.filters Return value of `useMembersFilters`.
 * @param {{ roles: object[], groups: object[], batches: object[] }} props.options Flat `{ value, label }` lists.
 * @param {() => void} props.onAllFiltersClick
 */
function MembersFilters({ filters, options, onAllFiltersClick }) {
  const { t } = useTranslation('people-access');
  const { search, status, role, group, mfa, added, importBatch, sort, activeFilterCount } = filters;
  const { setSearch, setStatus, setFilter, setSort, clearAll } = filters;

  const searchProps = useMemo(
    () => ({
      value: search,
      onChange: (event) => setSearch(event.target.value),
      onClear: () => setSearch(''),
      placeholder: t('members.filters.searchPlaceholder'),
      'aria-label': t('members.filters.searchPlaceholder'),
    }),
    [search, setSearch, t],
  );

  const mfaOptions = useMemo(
    () => [
      { value: MFA_FILTER.ON, label: t('members.mfa.on') },
      { value: MFA_FILTER.OFF, label: t('members.mfa.off') },
    ],
    [t],
  );

  const addedOptions = useMemo(
    () => ADDED_FILTER_OPTIONS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
    [t],
  );

  const slots = useMemo(() => {
    const base = [
      {
        id: 'role',
        label: t('members.filters.role'),
        multiple: true,
        value: role,
        onChange: (next) => setFilter('role', next),
        options: options.roles,
      },
      {
        id: 'group',
        label: t('members.filters.group'),
        multiple: true,
        value: group,
        onChange: (next) => setFilter('group', next),
        options: options.groups,
      },
      {
        id: 'mfa',
        label: t('members.columns.mfa'),
        multiple: false,
        value: toSlotValue(mfa),
        onChange: (next) => setFilter('mfa', fromSlotValue(next)),
        options: mfaOptions,
      },
      {
        id: 'added',
        label: t('members.columns.added'),
        multiple: false,
        value: toSlotValue(added),
        onChange: (next) => setFilter('added', fromSlotValue(next)),
        options: addedOptions,
      },
      {
        // Lives in the quick-filter chips and the slideout, not in the bar (it is past `maxFilters`
        // and has no options); it is listed so an active status makes "Clear filters" appear.
        id: 'status',
        label: t('members.filters.statusSection'),
        multiple: false,
        value: toSlotValue(status),
        onChange: (next) => setFilter('status', fromSlotValue(next)),
        options: NO_OPTIONS,
      },
    ];

    // Hidden entirely when no import exists. The bar shows at most four slots: an applied batch
    // chip takes the first one, otherwise the slot waits in the "All filters" slideout.
    if (!options.batches.length) return base;
    const batchSlot = {
      id: 'importBatch',
      label: t('members.filters.imported'),
      multiple: false,
      value: toSlotValue(importBatch),
      onChange: (next) => setFilter('importBatch', fromSlotValue(next)),
      options: options.batches,
    };
    return importBatch ? [batchSlot, ...base] : [...base, batchSlot];
  }, [t, role, group, mfa, added, status, importBatch, options, mfaOptions, addedOptions, setFilter]);

  const sortConfig = useMemo(
    () => ({
      label: t('members.filters.sortBy'),
      value: sort.field,
      defaultValue: MEMBERS_DEFAULT_SORT.field,
      defaultDirection: MEMBERS_DEFAULT_SORT.direction,
      direction: sort.direction,
      options: MEMBERS_SORT_FIELDS.map(({ value, labelKey }) => ({ value, label: t(labelKey) })),
      onChange: (field) => setSort(getSortForField(field, sort)),
      onDirectionChange: (direction) => setSort({ ...sort, direction }),
    }),
    [t, sort, setSort],
  );

  const i18n = useMemo(
    () => ({
      clearFilters: t('members.filters.clear'),
      allFilters: activeFilterCount
        ? t('members.filters.allFiltersCount', { count: activeFilterCount })
        : t('members.filters.allFilters'),
    }),
    [t, activeFilterCount],
  );

  const handleChip = useCallback((value) => setStatus(value), [setStatus]);

  return (
    <div className="flex flex-col gap-3">
      <Filters
        searchProps={searchProps}
        filters={slots}
        maxFilters={4}
        sort={sortConfig}
        viewToggle={VIEW_TOGGLE_HIDDEN}
        onClearAll={clearAll}
        onAllFiltersClick={onAllFiltersClick}
        i18n={i18n}
      />
      <div role="group" aria-label={t('members.filters.quickAriaLabel')} className="flex flex-wrap gap-2">
        {MEMBER_QUICK_FILTERS.map(({ value, labelKey }) => (
          <SelectableChip key={value || 'all'} selected={status === value} onClick={() => handleChip(value)}>
            {t(labelKey)}
          </SelectableChip>
        ))}
      </div>
    </div>
  );
}

export default memo(MembersFilters);
